import { type ReactNode } from 'react'
import { cn } from '@/shared/utils/cn'

interface TimelineRowProps {
  /** Nodo que se apoya sobre el riel (avatar, punto de estado…). */
  marker: ReactNode
  /** Dibuja el tramo de riel que baja hacia el siguiente nodo. */
  withRail?: boolean
  className?: string
  children: ReactNode
}

/**
 * Un nodo del riel. Geometría compartida por todas las formas de la línea de
 * tiempo: el marcador ocupa 32 px a la izquierda y la línea cae desde su borde
 * inferior (`left-4` = centro del marcador) hasta el nodo siguiente.
 *
 * Es un `<li>`: la cronología es una lista ordenada real, no `<div>` apilados.
 */
export function TimelineRow({
  marker,
  withRail = true,
  className,
  children,
}: TimelineRowProps) {
  return (
    // `tabIndex=-1` no mete el nodo en el orden de tabulación: lo hace
    // enfocable por código para que, al comentar, el foco (y el lector de
    // pantalla) aterricen en el mensaje recién publicado.
    <li
      tabIndex={-1}
      className={cn(
        'relative rounded-lg pl-11 outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    >
      {withRail && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-4 top-8 w-px bg-black/10"
        />
      )}
      <span className="absolute left-0 top-0">{marker}</span>
      <div className="min-w-0 pb-6">{children}</div>
    </li>
  )
}
